import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  ProgressDialog,
  ProgressState,
} from "../../../../components/ProgressDialog";
import { TabPanel } from "../../../../components/TabPanel";
import { Shape } from "../../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import { DecisionLogDisplay } from "../../TemporalTest/components/DecisionLogDisplay";
import { TimelineView } from "../../TemporalTest/components/TimelineView";
import {
  FilterBreakdown,
  LogEntry,
  ParkingRequest,
  Placement,
  Spot,
  Zone,
} from "../../TemporalTest/types";
import { PartialPlacementInfo } from "../utils/detectPartialPlacements";
import { CapacityBar } from "./CapacityBar";
import { PlacementTableView } from "./PlacementTableView";
import { ScheduleChange, SpotOccupancyGantt } from "./SpotOccupancyGantt";

interface DecisionLogDialogProps {
  open: boolean;
  onClose: () => void;
  decisionLog: LogEntry[];
  requests: ParkingRequest[];
  rampShapes?: Shape[];
  // Optional temporal data for timeline view
  spots?: Spot[];
  placement?: Placement;
  zones?: Zone[];
  horizonHours?: number;
  startTime?: Date;
  partialPlacements?: PartialPlacementInfo[];
  // Placement statistics
  statistics?: {
    totalAircraft: number;
    placedAircraft: number;
    unplacedAircraft: number;
    partiallyPlacedAircraft?: number;
    averageMoves?: number;
    elapsedMs?: number;
  };
  // Apply callback for persisting results to database
  onApply?: () => void | Promise<void>;
  // Loading state for apply operation
  isApplying?: boolean;
  // Progress state for apply operation
  applyProgress?: ProgressState;
  // Show detailed view with stats and decision log tab (default: false)
  showDetailedView?: boolean;
  // Callback for GANTT schedule changes
  onScheduleChanged?: (changes: ScheduleChange[]) => void;
}

export const DecisionLogDialog: React.FC<DecisionLogDialogProps> = React.memo(
  ({
    open,
    onClose,
    decisionLog,
    requests,
    rampShapes = [],
    spots,
    placement,
    zones,
    horizonHours = 24,
    startTime = new Date(),
    partialPlacements = [],
    statistics,
    onApply,
    isApplying,
    applyProgress,
    showDetailedView = false,
    onScheduleChanged,
  }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [placementFilter, setPlacementFilter] = useState<
      "all" | "placed" | "unplaced"
    >("all");

    // Create mapping from spot ID to spot name
    const spotIdToName = useMemo(() => {
      const mapping: Record<string, string> = {};
      rampShapes.forEach((shape) => {
        if (shape.type === "Spot" && shape.text) {
          mapping[shape.id] = shape.text;
        }
      });
      return mapping;
    }, [rampShapes]);

    // Check if we have temporal data for timeline view
    const hasTimelineData = Boolean(spots && placement);

    // Memoize requests with logs to avoid re-filtering on every render
    const requestsWithLogs = useMemo(() => {
      return requests.filter((request) => {
        const requestLogs = decisionLog.filter(
          (e) => e.aircraftId === request.id
        );
        return requestLogs.length > 0;
      });
    }, [requests, decisionLog]);

    // Helper function to determine if aircraft was placed or unplaced
    const getAircraftStatus = (
      request: ParkingRequest
    ): "placed" | "unplaced" => {
      const requestLogs = decisionLog.filter(
        (e) => e.aircraftId === request.id
      );
      const hasRejectedLog = requestLogs.some((e) => e.type === "rejected");
      return hasRejectedLog ? "unplaced" : "placed";
    };

    // Filter requests based on placement filter
    const filteredRequests = useMemo(() => {
      if (placementFilter === "all") {
        return requestsWithLogs;
      }
      return requestsWithLogs.filter((request) => {
        return getAircraftStatus(request) === placementFilter;
      });
    }, [requestsWithLogs, placementFilter, decisionLog]);

    // Calculate counts for each filter option
    const filterCounts = useMemo(() => {
      const placedCount = requestsWithLogs.filter(
        (r) => getAircraftStatus(r) === "placed"
      ).length;
      const unplacedCount = requestsWithLogs.filter(
        (r) => getAircraftStatus(r) === "unplaced"
      ).length;
      return {
        all: requestsWithLogs.length,
        placed: placedCount,
        unplaced: unplacedCount,
      };
    }, [requestsWithLogs, decisionLog]);

    // Calculate capacity data for CapacityBar
    const capacityData = useMemo(() => {
      // Total spots on ramp
      const totalSpots = rampShapes.filter((s) => s.type === "Spot").length;

      // Number of aircraft placed (same as filterCounts.placed)
      const placedCount = filterCounts.placed;

      // Aggregate filter breakdowns from all rejected aircraft
      // Note: We need to find the "worst case" breakdown - the one showing maximum constraints
      const rejectedLogs = decisionLog.filter((e) => e.type === "rejected");

      let aggregatedBreakdown: FilterBreakdown | undefined;
      if (rejectedLogs.length > 0) {
        // Find the rejection with the most comprehensive breakdown
        // (typically the first rejection will have full spot list)
        const breakdownLog = rejectedLogs.find((e) => e.filterBreakdown);
        if (breakdownLog && breakdownLog.filterBreakdown) {
          aggregatedBreakdown = breakdownLog.filterBreakdown;
        }
      }

      // Calculate available spots (spots that passed filters for at least one aircraft)
      // This is an approximation: totalSpots - placed - filtered
      const availableCount = aggregatedBreakdown
        ? Math.max(
            0,
            totalSpots -
              placedCount -
              (aggregatedBreakdown.total - aggregatedBreakdown.available)
          )
        : Math.max(0, totalSpots - placedCount);

      return {
        totalSpots,
        placedCount,
        availableCount,
        filterBreakdown: aggregatedBreakdown,
      };
    }, [rampShapes, filterCounts.placed, decisionLog]);

    return (
      <>
        <Dialog open={open} onClose={onClose} fullScreen>
          <DialogTitle>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label="close"
                sx={{ ml: "auto" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Tabs - only show when detailed view is enabled */}
            {hasTimelineData && showDetailedView && (
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}
              >
                <Tab label="Spot Occupancy" />
                <Tab label="List View" />
                <Tab label="Timeline (Dev)" />
                <Tab
                  label={`Decision Log (${filteredRequests.length} aircraft)`}
                />
              </Tabs>
            )}
          </DialogTitle>
          <DialogContent
            dividers={showDetailedView}
            sx={{
              p: showDetailedView ? 3 : 0,
              height: "100%",
            }}
          >
            {hasTimelineData ? (
              <>
                {/* Simple view: show only spot occupancy gantt */}
                {!showDetailedView && (
                  <SpotOccupancyGantt
                    spots={spots!}
                    aircraft={requests}
                    placement={placement!}
                    horizonHours={horizonHours}
                    startTime={startTime}
                    rampShapes={rampShapes}
                    zones={zones}
                    statistics={statistics}
                    partialPlacements={partialPlacements}
                    enableEditing={true}
                    onScheduleChanged={onScheduleChanged}
                    decisionLog={decisionLog}
                    showDetailedView={showDetailedView}
                  />
                )}

                {/* Detailed view: show tabs with stats and decision log */}
                {showDetailedView && (
                  <>
                    {/* Spot Occupancy Tab - Operational GANTT View */}
                    <TabPanel value={activeTab} index={0}>
                      {activeTab === 0 && (
                        <SpotOccupancyGantt
                          spots={spots!}
                          aircraft={requests}
                          placement={placement!}
                          horizonHours={horizonHours}
                          startTime={startTime}
                          rampShapes={rampShapes}
                          zones={zones}
                          statistics={statistics}
                          partialPlacements={partialPlacements}
                          enableEditing={true}
                          onScheduleChanged={onScheduleChanged}
                          decisionLog={decisionLog}
                          showDetailedView={showDetailedView}
                        />
                      )}
                    </TabPanel>

                    {/* List View Tab - Compact Table View */}
                    <TabPanel value={activeTab} index={1}>
                      {activeTab === 1 && (
                        <PlacementTableView
                          statistics={statistics}
                          placement={placement!}
                          requests={requests}
                          spots={spots!}
                          zones={zones}
                          rampShapes={rampShapes}
                          startTime={startTime}
                          horizonHours={horizonHours}
                          partialPlacements={partialPlacements}
                          enableEditing={true}
                          onScheduleChanged={onScheduleChanged}
                        />
                      )}
                    </TabPanel>

                    {/* Timeline (Dev) Tab */}
                    <TabPanel value={activeTab} index={2}>
                      {activeTab === 2 && (
                        <>
                          {/* Placement Statistics Cards */}
                          {statistics && (
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fit, minmax(200px, 1fr))",
                                gap: 2,
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  p: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  gutterBottom
                                >
                                  Aircraft Placed
                                </Typography>
                                <Typography variant="h4" component="div">
                                  {statistics.placedAircraft} /{" "}
                                  {statistics.totalAircraft}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  p: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  gutterBottom
                                >
                                  Success Rate
                                </Typography>
                                <Typography variant="h4" component="div">
                                  {Math.round(
                                    (statistics.placedAircraft /
                                      statistics.totalAircraft) *
                                      100
                                  )}
                                  %
                                </Typography>
                              </Box>

                              {statistics.unplacedAircraft > 0 && (
                                <Box
                                  sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    p: 2,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    Unplaced Aircraft
                                  </Typography>
                                  <Typography variant="h4" component="div">
                                    {statistics.unplacedAircraft}
                                  </Typography>
                                </Box>
                              )}

                              {statistics.partiallyPlacedAircraft !==
                                undefined &&
                                statistics.partiallyPlacedAircraft > 0 && (
                                  <Box
                                    sx={{
                                      border: 1,
                                      borderColor: "warning.main",
                                      borderRadius: 1,
                                      p: 2,
                                      bgcolor: "warning.lighter",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="warning.dark"
                                      gutterBottom
                                    >
                                      Partially Placed
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      component="div"
                                      color="warning.dark"
                                    >
                                      {statistics.partiallyPlacedAircraft}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="warning.dark"
                                      sx={{ mt: 0.5, display: "block" }}
                                    >
                                      Placed for part of trip
                                    </Typography>
                                  </Box>
                                )}

                              {statistics.averageMoves !== undefined &&
                                statistics.averageMoves > 0 && (
                                  <Box
                                    sx={{
                                      border: 1,
                                      borderColor: "divider",
                                      borderRadius: 1,
                                      p: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      gutterBottom
                                    >
                                      Average Moves
                                    </Typography>
                                    <Typography variant="h4" component="div">
                                      {statistics.averageMoves.toFixed(2)}
                                    </Typography>
                                  </Box>
                                )}

                              {statistics.elapsedMs !== undefined && (
                                <Box
                                  sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    p: 2,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    Execution Time
                                  </Typography>
                                  <Typography variant="h4" component="div">
                                    {statistics.elapsedMs < 1000
                                      ? `${Math.round(statistics.elapsedMs)}ms`
                                      : `${(
                                          statistics.elapsedMs / 1000
                                        ).toFixed(2)}s`}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                          <TimelineView
                            spots={spots!}
                            aircraft={requests}
                            placement={placement!}
                            log={decisionLog}
                            zones={zones}
                            horizonHours={horizonHours}
                            startTime={startTime}
                            spotIdToName={spotIdToName}
                          />
                        </>
                      )}
                    </TabPanel>

                    {/* Decision Log Tab */}
                    <TabPanel value={activeTab} index={3}>
                      <Box sx={{ maxHeight: "100%", overflow: "auto" }}>
                        {/* Capacity Bar - shows spot utilization at a glance */}
                        {capacityData.totalSpots > 0 && (
                          <CapacityBar
                            totalSpots={capacityData.totalSpots}
                            placedCount={capacityData.placedCount}
                            availableCount={capacityData.availableCount}
                            filterBreakdown={capacityData.filterBreakdown}
                          />
                        )}

                        {/* Placement Filter Chips */}
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="center"
                          sx={{ mb: 2 }}
                        >
                          <Chip
                            label={`All (${filterCounts.all})`}
                            variant={
                              placementFilter === "all" ? "filled" : "outlined"
                            }
                            clickable
                            onClick={() => setPlacementFilter("all")}
                            sx={{
                              fontSize: "0.875rem",
                              height: "32px",
                              borderRadius: "16px",
                              fontWeight: 500,
                              transition: "all 0.2s ease-in-out",
                              "&.MuiChip-outlined": {
                                borderColor: "divider",
                                backgroundColor: "transparent",
                                color: "text.secondary",
                                "&:hover": {
                                  borderColor: "primary.main",
                                  backgroundColor: "action.hover",
                                  color: "text.primary",
                                },
                              },
                              "&.MuiChip-filled": {
                                backgroundColor: "primary.main",
                                borderColor: "primary.main",
                                color: "primary.contrastText",
                                fontWeight: 600,
                                "&:hover": {
                                  backgroundColor: "primary.dark",
                                  borderColor: "primary.dark",
                                },
                              },
                            }}
                          />
                          <Chip
                            label={`Placed (${filterCounts.placed})`}
                            variant={
                              placementFilter === "placed"
                                ? "filled"
                                : "outlined"
                            }
                            clickable
                            onClick={() => setPlacementFilter("placed")}
                            sx={{
                              fontSize: "0.875rem",
                              height: "32px",
                              borderRadius: "16px",
                              fontWeight: 500,
                              transition: "all 0.2s ease-in-out",
                              "&.MuiChip-outlined": {
                                borderColor: "divider",
                                backgroundColor: "transparent",
                                color: "text.secondary",
                                "&:hover": {
                                  borderColor: "success.main",
                                  backgroundColor: "action.hover",
                                  color: "text.primary",
                                },
                              },
                              "&.MuiChip-filled": {
                                backgroundColor: "success.main",
                                borderColor: "success.main",
                                color: "success.contrastText",
                                fontWeight: 600,
                                "&:hover": {
                                  backgroundColor: "success.dark",
                                  borderColor: "success.dark",
                                },
                              },
                            }}
                          />
                          <Chip
                            label={`Unplaced (${filterCounts.unplaced})`}
                            variant={
                              placementFilter === "unplaced"
                                ? "filled"
                                : "outlined"
                            }
                            clickable
                            onClick={() => setPlacementFilter("unplaced")}
                            sx={{
                              fontSize: "0.875rem",
                              height: "32px",
                              borderRadius: "16px",
                              fontWeight: 500,
                              transition: "all 0.2s ease-in-out",
                              "&.MuiChip-outlined": {
                                borderColor: "divider",
                                backgroundColor: "transparent",
                                color: "text.secondary",
                                "&:hover": {
                                  borderColor: "error.main",
                                  backgroundColor: "action.hover",
                                  color: "text.primary",
                                },
                              },
                              "&.MuiChip-filled": {
                                backgroundColor: "error.main",
                                borderColor: "error.main",
                                color: "error.contrastText",
                                fontWeight: 600,
                                "&:hover": {
                                  backgroundColor: "error.dark",
                                  borderColor: "error.dark",
                                },
                              },
                            }}
                          />
                        </Stack>

                        {filteredRequests.map((request) => (
                          <Box key={request.id} sx={{ mb: 2 }}>
                            <DecisionLogDisplay
                              aircraft={request}
                              log={decisionLog}
                              spotIdToName={spotIdToName}
                            />
                          </Box>
                        ))}
                      </Box>
                    </TabPanel>
                  </>
                )}
              </>
            ) : (
              <Box sx={{ maxHeight: "100%", overflow: "auto" }}>
                {/* Capacity Bar - shows spot utilization at a glance */}
                {capacityData.totalSpots > 0 && (
                  <CapacityBar
                    totalSpots={capacityData.totalSpots}
                    placedCount={capacityData.placedCount}
                    availableCount={capacityData.availableCount}
                    filterBreakdown={capacityData.filterBreakdown}
                  />
                )}

                {/* Placement Filter Chips */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ mb: 2 }}
                >
                  <Chip
                    label={`All (${filterCounts.all})`}
                    variant={placementFilter === "all" ? "filled" : "outlined"}
                    clickable
                    onClick={() => setPlacementFilter("all")}
                    sx={{
                      fontSize: "0.875rem",
                      height: "32px",
                      borderRadius: "16px",
                      fontWeight: 500,
                      transition: "all 0.2s ease-in-out",
                      "&.MuiChip-outlined": {
                        borderColor: "divider",
                        backgroundColor: "transparent",
                        color: "text.secondary",
                        "&:hover": {
                          borderColor: "primary.main",
                          backgroundColor: "action.hover",
                          color: "text.primary",
                        },
                      },
                      "&.MuiChip-filled": {
                        backgroundColor: "primary.main",
                        borderColor: "primary.main",
                        color: "primary.contrastText",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "primary.dark",
                          borderColor: "primary.dark",
                        },
                      },
                    }}
                  />
                  <Chip
                    label={`Placed (${filterCounts.placed})`}
                    variant={
                      placementFilter === "placed" ? "filled" : "outlined"
                    }
                    clickable
                    onClick={() => setPlacementFilter("placed")}
                    sx={{
                      fontSize: "0.875rem",
                      height: "32px",
                      borderRadius: "16px",
                      fontWeight: 500,
                      transition: "all 0.2s ease-in-out",
                      "&.MuiChip-outlined": {
                        borderColor: "divider",
                        backgroundColor: "transparent",
                        color: "text.secondary",
                        "&:hover": {
                          borderColor: "success.main",
                          backgroundColor: "action.hover",
                          color: "text.primary",
                        },
                      },
                      "&.MuiChip-filled": {
                        backgroundColor: "success.main",
                        borderColor: "success.main",
                        color: "success.contrastText",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "success.dark",
                          borderColor: "success.dark",
                        },
                      },
                    }}
                  />
                  <Chip
                    label={`Unplaced (${filterCounts.unplaced})`}
                    variant={
                      placementFilter === "unplaced" ? "filled" : "outlined"
                    }
                    clickable
                    onClick={() => setPlacementFilter("unplaced")}
                    sx={{
                      fontSize: "0.875rem",
                      height: "32px",
                      borderRadius: "16px",
                      fontWeight: 500,
                      transition: "all 0.2s ease-in-out",
                      "&.MuiChip-outlined": {
                        borderColor: "divider",
                        backgroundColor: "transparent",
                        color: "text.secondary",
                        "&:hover": {
                          borderColor: "error.main",
                          backgroundColor: "action.hover",
                          color: "text.primary",
                        },
                      },
                      "&.MuiChip-filled": {
                        backgroundColor: "error.main",
                        borderColor: "error.main",
                        color: "error.contrastText",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "error.dark",
                          borderColor: "error.dark",
                        },
                      },
                    }}
                  />
                </Stack>

                {filteredRequests.map((request) => (
                  <Box key={request.id} sx={{ mb: 2 }}>
                    <DecisionLogDisplay
                      aircraft={request}
                      log={decisionLog}
                      spotIdToName={spotIdToName}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>

          {/* Dialog Actions */}
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose} disabled={isApplying}>
              Close
            </Button>
            {onApply && hasTimelineData && (
              <Button
                onClick={onApply}
                variant="contained"
                color="primary"
                startIcon={
                  isApplying ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <CheckIcon />
                  )
                }
                disabled={isApplying}
              >
                {isApplying ? "Applying..." : "Apply to Ramp"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Progress Dialog for Apply Operation */}
        {isApplying && applyProgress && applyProgress.total > 0 && (
          <ProgressDialog
            open={isApplying}
            title="Applying Results to Database"
            message="Saving aircraft placements to the database. Please wait..."
            current={applyProgress.current}
            total={applyProgress.total}
            itemName="placement"
            currentItem={applyProgress.currentItem}
          />
        )}
      </>
    );
  }
);
